import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { ItemBusca } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { useCategorias, useOrcamento } from "../api/consultas";
import { useAvisos } from "../components/Avisos";
import { useAdicionarItem } from "../components/orcamento";
import { useTermoComAtraso } from "../components/useTermoComAtraso";
import { Carregando, ErroCarregamento, formatarMoeda, lerNumero, semAutocompletar, Tabela } from "../components/ui";
import { useIdOrcamento } from "./OrcamentoDetalhe";

/** Sugestão de poços = extensão ÷ espaçamento + 1 (espaçamento informado pelo usuário). */
function pocosSugeridos(extensao: number, espacamento: number): number | null {
  if (!Number.isFinite(extensao) || !Number.isFinite(espacamento) || espacamento <= 0) return null;
  return Math.floor(extensao / espacamento) + 1;
}

function buscarOpcoes(orcamentoId: number, categoria: string, q = "") {
  const params = new URLSearchParams({ categoria, orcamento_id: String(orcamentoId), q });
  return api<ItemBusca[]>(`/api/compor/opcoes?${params}`);
}

export function OrcamentoCompor() {
  const id = useIdOrcamento();
  const orcamento = useOrcamento(id);
  const categorias = useCategorias();
  const navigate = useNavigate();
  const { avisar } = useAvisos();
  const adicionar = useAdicionarItem(id);

  const [categoria, setCategoria] = useState("");
  const [qOutros, setQOutros] = useState("");
  const termoOutros = useTermoComAtraso(qOutros, 250, 3);
  const [selecionado, setSelecionado] = useState<ItemBusca | null>(null);
  const [quantidade, setQuantidade] = useState("1");
  const [espacamento, setEspacamento] = useState("");
  const [pocos, setPocos] = useState<{ quantidade: number | null; calculado: boolean }>({ quantidade: null, calculado: false });

  const opcoes = useQuery({
    queryKey: ["compor", id, categoria, categoria === "outros" ? termoOutros : ""],
    queryFn: () => buscarOpcoes(id, categoria, categoria === "outros" ? termoOutros : ""),
    enabled: Boolean(categoria) && (categoria !== "outros" || termoOutros.length >= 3),
  });
  const opcoesOrdenadas = useMemo(
    () => [...(opcoes.data ?? [])].sort((a, b) => (a.diametro_mm || 0) - (b.diametro_mm || 0)),
    [opcoes.data],
  );

  const opcoesPocos = useQuery({
    queryKey: ["compor", id, "pocos_caixas", ""],
    queryFn: () => buscarOpcoes(id, "pocos_caixas"),
    enabled: pocos.quantidade !== null,
  });

  if (orcamento.isLoading || categorias.isLoading) return <Carregando />;
  if (orcamento.error || !orcamento.data) return <ErroCarregamento erro={orcamento.error} />;
  const { orcamento: orc } = orcamento.data;
  const qtd = lerNumero(quantidade);

  const adicionarEVoltar = (itemId: number, q: number, codigo: string) =>
    adicionar.mutate(
      { item_id: itemId, quantidade: q },
      {
        onSuccess: () => {
          avisar(`${codigo} adicionado ao orçamento.`);
          navigate(`/orcamentos/${id}`);
        },
      },
    );

  return (
    <>
      <p className="note">
        <Link to={`/orcamentos/${id}`}>&larr; Voltar ao orçamento</Link>
      </p>
      <h1>Compor Serviço</h1>
      <p className="note">
        Escolha o tipo de serviço, informe a extensão (ou quantidade) e o sistema busca, na tabela oficial já importada
        (regime <strong>{orc.regime}</strong>), os itens reais compatíveis — o preço e o código vêm sempre da tabela
        oficial, nunca de um valor calculado por fora dela.
      </p>

      <div className="card">
        <div className="form-row">
          <label>
            1. Tipo de serviço
            <select
              style={{ minWidth: 280 }}
              value={categoria}
              onChange={(e) => {
                setCategoria(e.target.value);
                setSelecionado(null);
                setQOutros("");
                setPocos({ quantidade: null, calculado: false });
              }}
            >
              <option value="">— selecione —</option>
              {(categorias.data ?? []).map((c) => (
                <option key={c.chave} value={c.chave}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
        </div>

        {categoria === "outros" && (
          <div className="form-row">
            <label>
              Digite o serviço que você procura
              <input
                type="search"
                autoFocus
                placeholder="ex: execução de obra, escavação, concreto..."
                style={{ minWidth: 320 }}
                value={qOutros}
                onChange={(e) => setQOutros(e.target.value)}
                {...semAutocompletar}
              />
            </label>
          </div>
        )}

        {opcoes.isFetching && <p className="note">Buscando…</p>}
        {opcoesOrdenadas.length > 0 && (
          <div>
            <h3>2. Escolha a opção compatível encontrada na tabela oficial</h3>
            <Tabela>
              <thead>
                <tr>
                  <th />
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Diâmetro</th>
                  <th>Material</th>
                  <th>Un.</th>
                  <th className="num">Preço</th>
                </tr>
              </thead>
              <tbody>
                {opcoesOrdenadas.map((r, i) => (
                  <tr key={`${r.id}-${i}`} style={{ cursor: "pointer" }} onClick={() => setSelecionado(r)}>
                    <td>
                      <input type="radio" name="opcao" readOnly checked={selecionado?.id === r.id} />
                    </td>
                    <td>{r.codigo}</td>
                    <td>{r.descricao}</td>
                    <td>{r.diametro_mm ? `${r.diametro_mm} mm` : "—"}</td>
                    <td>{r.material || "—"}</td>
                    <td>{r.unidade}</td>
                    <td className="num">{formatarMoeda(r.preco_unitario)}</td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
          </div>
        )}

        {selecionado && (
          <div>
            <h3>3. Informe a extensão / quantidade</h3>
            <div className="form-row">
              <label>
                {selecionado.unidade === "M" ? "Extensão (m)" : `Quantidade (${selecionado.unidade})`}
                <input type="text" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} {...semAutocompletar} />
              </label>
              <span className="note" style={{ alignSelf: "center" }}>
                Total: R$ {formatarMoeda(qtd * selecionado.preco_unitario)}
              </span>
              <button
                type="button"
                className="btn-orange"
                disabled={adicionar.isPending}
                onClick={() => adicionarEVoltar(selecionado.id, qtd, selecionado.codigo)}
              >
                Adicionar ao orçamento
              </button>
            </div>
          </div>
        )}
      </div>

      {["rede_esgoto", "adutora_agua"].includes(categoria) && (
        <div className="card">
          <h3>Opcional: calcular quantidade de poços de visita / caixas</h3>
          <p className="note">
            O espaçamento é o que você definir para o projeto (norma/projeto aplicável) — o sistema só calcula, não assume
            nenhum valor por conta própria.
          </p>
          <div className="form-row">
            <label>
              Espaçamento entre poços (m)
              <input type="text" placeholder="ex: 100" value={espacamento} onChange={(e) => setEspacamento(e.target.value)} {...semAutocompletar} />
            </label>
            <button
              type="button"
              onClick={() => setPocos({ quantidade: pocosSugeridos(lerNumero(quantidade), lerNumero(espacamento)), calculado: true })}
            >
              Calcular quantidade sugerida
            </button>
            <span className="note">
              {pocos.calculado &&
                (pocos.quantidade === null ? "Informe um espaçamento válido." : `Quantidade sugerida: ${pocos.quantidade} poço(s)`)}
            </span>
          </div>
          {pocos.quantidade !== null && (
            <Tabela>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Un.</th>
                  <th className="num">Preço</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(opcoesPocos.data ?? []).map((r, i) => (
                  <tr key={`${r.id}-${i}`}>
                    <td>{r.codigo}</td>
                    <td>{r.descricao}</td>
                    <td>{r.unidade}</td>
                    <td className="num">{formatarMoeda(r.preco_unitario)}</td>
                    <td>
                      <button type="button" disabled={adicionar.isPending} onClick={() => adicionarEVoltar(r.id, pocos.quantidade!, r.codigo)}>
                        + Add ({pocos.quantidade})
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
          )}
        </div>
      )}
    </>
  );
}
